/**
 * Data Access Layer for Staff Roles
 *
 * A generic, account-scoped role catalog (stage manager, artist care, sound
 * engineer, …) plus the person ↔ role qualification join.
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type { StaffRole, StaffRoleCreate, StaffRoleUpdate } from './types.js';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

// ============================================
// Role catalog
// ============================================

export async function getStaffRole(id: string): Promise<StaffRole | null> {
  const { data, error } = await getClient()
    .from(TABLES.staff_roles)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as StaffRole;
}

export async function listStaffRoles(): Promise<StaffRole[]> {
  let query = getClient().from(TABLES.staff_roles).select('*');

  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);

  const { data, error } = await query.order('name');
  if (error) throw error;
  return (data || []) as StaffRole[];
}

export async function createStaffRole(input: StaffRoleCreate): Promise<StaffRole> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('createStaffRole: no account context set');

  const { data, error } = await getClient()
    .from(TABLES.staff_roles)
    .insert({
      account_id: accountId,
      name: input.name,
      slug: input.slug?.trim() || slugify(input.name),
    })
    .select()
    .single();

  if (error) throw error;
  return data as StaffRole;
}

export async function updateStaffRole(id: string, input: StaffRoleUpdate): Promise<StaffRole> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name;
  if (input.slug !== undefined) patch.slug = slugify(input.slug);

  const { data, error } = await getClient()
    .from(TABLES.staff_roles)
    .update(patch)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as StaffRole;
}

export async function deleteStaffRole(id: string): Promise<void> {
  const { error } = await getClient().from(TABLES.staff_roles).delete().eq('id', id);
  if (error) throw error;
}

// ============================================
// Person ↔ role qualifications
// ============================================

export async function listPersonRoles(personId: string): Promise<StaffRole[]> {
  const { data, error } = await getClient()
    .from(TABLES.person_roles)
    .select('role:role_id (*)')
    .eq('person_id', personId);

  if (error) throw error;
  return (data || [])
    .map((row: any) => row.role)
    .filter(Boolean) as StaffRole[];
}

export async function listPersonRolesBatch(
  personIds: string[],
): Promise<Record<string, StaffRole[]>> {
  if (personIds.length === 0) return {};

  const { data, error } = await getClient()
    .from(TABLES.person_roles)
    .select('person_id, role:role_id (*)')
    .in('person_id', personIds);

  if (error) throw error;

  const byPerson: Record<string, StaffRole[]> = {};
  for (const row of (data || []) as any[]) {
    if (!row.role) continue;
    (byPerson[row.person_id] ??= []).push(row.role as StaffRole);
  }
  return byPerson;
}

/** Replace a person's full set of role qualifications in one go. */
export async function setPersonRoles(personId: string, roleIds: string[]): Promise<void> {
  const client = getClient();

  const { error: deleteError } = await client
    .from(TABLES.person_roles)
    .delete()
    .eq('person_id', personId);
  if (deleteError) throw deleteError;

  if (roleIds.length > 0) {
    const rows = roleIds.map((role_id) => ({ person_id: personId, role_id }));
    const { error: insertError } = await client.from(TABLES.person_roles).insert(rows);
    if (insertError) throw insertError;
  }
}
